import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setSessionExpiredHandler, tokenStore } from '../api/client';
import type { AuthResponse, Role, User } from '../types';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<User>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
  canWrite: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const USER_KEY = 'ims.user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    tokenStore.clear();
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(logout);
  }, [logout]);

  // A reload leaves the in-memory access token empty; the stored refresh token restores the session.
  useEffect(() => {
    const cached = sessionStorage.getItem(USER_KEY);
    const refreshToken = tokenStore.refresh;

    if (!cached || !refreshToken) {
      setLoading(false);
      return;
    }

    api
      .post<AuthResponse>('/auth/refresh', { refreshToken })
      .then(({ data }) => {
        tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken ?? refreshToken });
        setUser(data.user ?? (JSON.parse(cached) as User));
      })
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [logout]);

  const login = useCallback(async (username: string, password: string) => {
    const { data } = await api.post<AuthResponse>('/auth/login', { username, password });
    tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      hasRole: (...roles: Role[]) => (user ? roles.includes(user.role) : false),
      canWrite: user?.role === 'Admin' || user?.role === 'Accountant',
    }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
